import type { ScheduledAction } from "@station-keeping/sim";

export interface Batch {
  /** Tick décrit par l'empreinte : l'état au début de ce tick, avant ses actions. */
  tick: number;
  hash: string;
  actions: ScheduledAction[];
}

/**
 * Le prochain lot à envoyer. Le serveur n'accepte que des actions datées d'avant le tick annoncé : celles du
 * tick courant attendent le lot suivant. Au-delà de `max` actions, le lot se coupe entre deux ticks, avec
 * pour empreinte celle du tick de la première action laissée de côté (le moteur la garde à chaque action).
 */
export function nextBatch(
  outbox: readonly ScheduledAction[],
  boundary: { tick: number; hash: string },
  boundaries: ReadonlyMap<number, string>,
  max: number,
): Batch {
  const ready = outbox.filter((scheduled) => scheduled.tick < boundary.tick);
  if (ready.length <= max) return { tick: boundary.tick, hash: boundary.hash, actions: ready };

  for (let cut = max; cut > 0; cut--) {
    const first = ready[cut] as ScheduledAction;
    const last = ready[cut - 1] as ScheduledAction;
    const hash = boundaries.get(first.tick);
    if (first.tick > last.tick && hash !== undefined) {
      return { tick: first.tick, hash, actions: ready.slice(0, cut) };
    }
  }
  // Impossible en jeu (le moteur limite les actions d'un même tick) : un battement seul, sans action.
  const oldest = ready[0] as ScheduledAction;
  return { tick: oldest.tick, hash: boundaries.get(oldest.tick) ?? boundary.hash, actions: [] };
}
